import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Download, History } from 'lucide-react';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { useNotice } from '../../../../hooks/useNotice';
import {
  exportMovements,
  fetchMovements,
  fetchMovementsFilters,
} from '../../../../features/movements/movementsThunks';
import {
  buildMovementsExportRows,
  exportDoneNotice,
  movementsExportFileName,
} from '../../../../features/movements/movementsExport';
import { clearMovementsFilters, setMovementsFilters } from '../../../../features/movements/movementsSlice';
import {
  actorOptions,
  formatMovementMoment,
  isFiltering,
  MOVEMENT_TYPE_OPTIONS,
} from '../../../../features/movements/movementsUtils';
import { ALL_FILTER } from '../../../../features/stock/stockConstants';
import {
  selectCurrentWarehouse,
  selectWarehouseClientOptions,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity, movementDetailLabel, movementTypeLabel } from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';

/**
 * `/warehouse/movements`: the whole kardex, every SKU, newest first.
 *
 * Inventario opens the history of one product; this is the same ledger read
 * across the floor — what happened in a location, what somebody did today,
 * every adjustment of the month. The server filters and paginates; the page
 * only says what it is asking for.
 */
export const Movements = () => {
  const dispatch = useDispatch();
  const { rows, totalCount, loading, error, filters, actors, hasSystem, exporting } = useSelector(
    (state) => state.movements
  );
  const warehouse = useSelector(selectCurrentWarehouse);
  const clientOptions = useSelector(selectWarehouseClientOptions);
  const setNotice = useNotice('movements-notice');

  const [searchInput, setSearchInput] = useState(filters.search);
  const [slotInput, setSlotInput] = useState(filters.slot);

  // Debounce for the two text filters
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setMovementsFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const slot = slotInput.trim();
      if (slot !== filters.slot) dispatch(setMovementsFilters({ slot }));
    }, 500);
    return () => clearTimeout(handler);
  }, [slotInput, filters.slot, dispatch]);

  // Who can be filtered by depends on the warehouse in use.
  useEffect(() => {
    dispatch(fetchMovementsFilters());
  }, [dispatch, warehouse?.id]);

  // Fetch when the warehouse in use or a filter changes
  useEffect(() => {
    dispatch(fetchMovements());
  }, [
    dispatch,
    warehouse?.id,
    filters.search,
    filters.slot,
    filters.company,
    filters.movementType,
    filters.actor,
    filters.dateFrom,
    filters.dateTo,
    filters.page,
    filters.rowsPerPage,
  ]);

  const handleFilterChange = useCallback((patch) => dispatch(setMovementsFilters(patch)), [dispatch]);

  const handleClear = () => {
    setSearchInput('');
    setSlotInput('');
    dispatch(clearMovementsFilters());
  };

  // The whole filter, not the page: the server is walked page by page.
  const handleExport = async () => {
    const action = await dispatch(exportMovements());
    if (action.meta.requestStatus !== 'fulfilled') return;
    const { rows: all, total } = action.payload;
    const sheet = XLSX.utils.json_to_sheet(buildMovementsExportRows(all));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Movimientos');
    XLSX.writeFile(book, movementsExportFileName(warehouse, filters));
    setNotice({ type: 'success', message: exportDoneNotice(all.length, total) });
  };

  const showClient = clientOptions.length > 1;
  const filtering = isFiltering(filters);

  const columns = [
    {
      header: 'FECHA',
      cell: (row) => <span className="text-text-secondary">{formatMovementMoment(row.occurred_at)}</span>,
    },
    ...(showClient
      ? [
          {
            header: 'CLIENTE',
            cell: (row) => <span className="text-text-secondary">{row.company_name || '-'}</span>,
          },
        ]
      : []),
    {
      // The product links to its own kardex: this screen is the floor, that one is the SKU.
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[260px]">
          <Link
            to={`${INVENTORY_PATH}/${row.product}`}
            className="font-bold font-mono text-ninja-dark dark:text-white hover:text-ninja-green"
            data-testid="movement-product"
          >
            {row.product_sku}
          </Link>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product_name}>
            {row.product_name}
          </span>
        </div>
      ),
    },
    {
      header: 'MOVIMIENTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[260px]" title={row.notes || undefined}>
          <span className="font-medium text-ninja-dark dark:text-white" data-testid="movement-type">
            {movementTypeLabel(row.movement_type)}
          </span>
          <span className="text-xs text-text-muted mt-0.5 truncate" data-testid="movement-detail">
            {movementDetailLabel(row)}
          </span>
          {/* The reason, in sight: an adjustment without it is a number nobody can explain. */}
          {row.notes && (
            <span className="text-xs text-text-secondary italic mt-0.5 truncate" data-testid="movement-notes">
              {row.notes}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'UBICACIÓN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-ninja-dark dark:text-white" data-testid="movement-slot">
            {row.slot_name || 'Sin ubicar'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">
            {[row.warehouse_name, row.lot && `Lote ${row.lot}`].filter(Boolean).join(' · ')}
          </span>
        </div>
      ),
    },
    {
      header: 'CANTIDAD',
      cell: (row) => {
        const delta = Number(row.qty_delta);
        return (
          <span
            data-testid="movement-delta"
            className={clsx('font-semibold tabular-nums', delta < 0 ? 'text-danger' : 'text-success')}
          >
            {delta > 0 ? '+' : ''}
            {formatQuantity(row.qty_delta)}
          </span>
        );
      },
    },
    {
      header: 'SALDO',
      cell: (row) => (
        <span className="tabular-nums">
          {row.balance_after === null ? '-' : formatQuantity(row.balance_after)}
        </span>
      ),
    },
    {
      header: 'QUIÉN',
      cell: (row) => (
        <span className="text-text-secondary" data-testid="movement-actor">
          {row.actor_email || 'sistema'}
        </span>
      ),
    },
  ];

  // An empty table has to say why: empty looks the same as broken.
  const emptyMessage = filtering
    ? 'No hay movimientos que coincidan con estos filtros.'
    : warehouse
      ? `Todavía no hay movimientos en ${warehouse.name}.`
      : 'Todavía no hay movimientos en tus bodegas.';

  return (
    <div data-testid="movements-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={History}
        title="Movimientos"
        description="El kardex completo: cada entrada, salida y traslado de todos los SKU, del más reciente al más antiguo."
      />

      <FilterBar
        variant="bare"
        testId="movements-filters"
        actions={
          <>
            {filtering && (
              <Button variant="ghost" size="sm" onClick={handleClear} data-testid="movements-clear">
                Limpiar filtros
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              icon={Download}
              onClick={handleExport}
              loading={exporting}
              loadingLabel="Exportando..."
              disabled={rows.length === 0}
              title={
                rows.length > 0
                  ? 'Excel de todos los movimientos de este filtro, no solo de esta página'
                  : 'No hay movimientos que exportar'
              }
              data-testid="movements-export"
            >
              Exportar
            </Button>
          </>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por SKU o nombre del producto..."
          testId="movements-search"
        />
        <SearchInput
          variant="bare"
          value={slotInput}
          onChange={setSlotInput}
          placeholder="Ubicación (nombre o código)..."
          testId="movements-slot"
        />
        {showClient && (
          <FilterableSelect
            label="Cliente:"
            value={filters.company}
            onChange={(company) => handleFilterChange({ company })}
            options={[{ value: ALL_FILTER, label: 'Todos' }, ...clientOptions]}
            searchPlaceholder="Buscar cliente..."
            panelMinWidth={240}
            testId="movements-client"
          />
        )}
        <FilterableSelect
          label="Tipo:"
          value={filters.movementType}
          onChange={(movementType) => handleFilterChange({ movementType })}
          options={MOVEMENT_TYPE_OPTIONS}
          testId="movements-type"
        />
        <FilterableSelect
          label="Quién:"
          value={filters.actor}
          onChange={(actor) => handleFilterChange({ actor })}
          options={actorOptions(actors, hasSystem)}
          searchPlaceholder="Buscar persona..."
          panelMinWidth={260}
          testId="movements-actor"
        />
        <DateRangePicker
          startDate={filters.dateFrom}
          endDate={filters.dateTo}
          onChange={({ start_date, end_date }) =>
            handleFilterChange({ dateFrom: start_date, dateTo: end_date })
          }
        />
      </FilterBar>

      <p className="text-sm text-text-secondary">
        El kardex no se edita: una corrección es un movimiento nuevo. Cada SKU abre su propio
        historial.
      </p>

      <DataTable
        columns={columns}
        data={rows}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchMovements())}
        itemLabel="movimientos"
        emptyMessage={emptyMessage}
        onPageChange={(page) => handleFilterChange({ page })}
        onRowsPerPageChange={(rowsPerPage) => handleFilterChange({ rowsPerPage })}
      />
    </div>
  );
};
